import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { TravelZoneEntity } from '../../../dave-data-module/entities/travel-zone.entity';
import { getFetched$ } from '../../../dave-data-module/helper/helper';
import { TravelZoneDataService } from '../../../dave-data-module/services/travel-zone-data.service';
import { State } from '../../../dave-data-module/State';
import { getTravelZoneById, getTravelZonesFetched } from '../../../dave-data-module/State/selectors/travel-zone.selector';
import { AppButtonModule } from '../../../dave-utils-module/app-button-module/app-button.module';
import { SelectSearchOption } from '../../../dave-utils-module/select-search/components/select-search/select-search.component';
import { SelectSearchModule } from '../../../dave-utils-module/select-search/select-search.module';
import { LoadingService } from '../../../services/loading.service';
import { EditTravelZoneComponent, EditTravelZoneComponentDialogData, EditTravelZoneComponentDialogReturnData } from '../edit-travel-zone/edit-travel-zone.component';
import { BookingPositionMetaIcon } from '../../../helper/page-metadata';

@Component({
    selector: 'app-travel-zone-select',
    standalone: true,
    imports: [CommonModule, SelectSearchModule, ReactiveFormsModule, AppButtonModule, FontAwesomeModule, MatTooltipModule, FormsModule, MatInputModule],
    templateUrl: './travel-zone-select.component.html',
    styleUrls: ['./travel-zone-select.component.scss'],
})
export class TravelZoneSelectComponent implements OnInit, OnDestroy {
    protected formControlValueChangeSubscription: Subscription;
    protected _formControl: FormControl<number>;
    @Input() set formControl(v: FormControl<number>) {
        this._formControl = v;
        this.formControlValueChangeSubscription?.unsubscribe();
        this.formControlValueChangeSubscription = v.valueChanges.pipe(switchMap((id) => (id ? getFetched$(this.store, getTravelZonesFetched, getTravelZoneById({ id })) : of(null)))).subscribe((value) => {
            this.value$.next(value);
        });
    }
    protected TravelZoneForm : FormControl<TravelZoneEntity>
    private requiredId$ = new BehaviorSubject<number>(null);
    @Input() set requiredId(v: number) {
        if (v !== this.requiredId$.value) {
            this.requiredId$.next(v);
        }
    }
    protected value$ = new BehaviorSubject<TravelZoneEntity>(null);
    protected options$: Observable<TravelZoneEntity[]>;
    protected readonly TravelZoneEntity = TravelZoneEntity;

    private destroy$ = new Subject<void>();

    constructor(private travelZoneDataService: TravelZoneDataService, private store: Store<State>, private dialog: MatDialog, private ls: LoadingService, private actions: Actions) {}

    compareById = (a: SelectSearchOption, b: SelectSearchOption) => a?.Id === b?.Id;

    ngOnInit() {
        this.options$ = this.requiredId$.pipe(switchMap((requiredId) => this.travelZoneDataService.getTravelZoneOptions$(requiredId)));

        if (this._formControl?.value) {
            getFetched$(this.store, getTravelZonesFetched, getTravelZoneById({ id: this._formControl.value })).pipe(takeUntil(this.destroy$))
                .subscribe((val) => this.value$.next(val));
        }
    }

    onEditClick(tz: TravelZoneEntity) {
        this.dialog.open<EditTravelZoneComponent, EditTravelZoneComponentDialogData>(EditTravelZoneComponent, {
            ...EditTravelZoneComponent.DefaultConfig,
            data: {
                travelZoneId: tz.Id,
            },
        });
    }

    valueChanged(v: TravelZoneEntity) {
        this._formControl.setValue(v?.Id || null);
        this._formControl.markAsDirty();
    }

    ngOnDestroy(): void {
        this.formControlValueChangeSubscription?.unsubscribe();

        this.destroy$.next();
        this.destroy$.complete();
    }

    async onAddClick() {
        this.dialog
            .open<EditTravelZoneComponent, EditTravelZoneComponentDialogData, EditTravelZoneComponentDialogReturnData>(EditTravelZoneComponent, {
                ...EditTravelZoneComponent.DefaultConfig,
                data: {
                    travelZoneId: null,
                },
            })
            .afterClosed()
            .subscribe((ret) => {
                if (ret) {
                    this.valueChanged(ret);
                }
            });
    }

    protected readonly BookingPositionMetaIcon = BookingPositionMetaIcon;
}
